// The chosen set: the stations somebody deliberately starred.
//
// THERE WAS NO EXPLICIT SAVE IN THIS APP. `DeparturesPage.pick()` called
// `addStation` and `makeDefault` in the same motion, so every station anybody
// switched the board to was silently saved AND silently made the station the
// app opens on. Ask how you save a station and the honest answer was that you
// cannot: it happens to you. What called itself SAVED was a visit history
// wearing the name of a chosen list — which is exactly why RECENT sat above it
// in the sheet, and exactly why a star beside it would have been a second save
// and a worse app.
//
// So the two collapse into one. A STAR IS THE SAVE. It is the only way a
// station joins the set, it is the only way one leaves, and switching the
// board to a station no longer joins anybody to anything.
//
// THE STORE ROW IS THE STAR, and this file is only the rules around it. The
// chosen set lives where it already lived — the generated `station` rows in
// `src/store.js`, per city through the row's own `city` — because those rows
// are what the widget and the watch already read (`src/snapshot.js` carries
// them, `src/snapshot-bridge.js` picks the default out of them) and what four
// screens already read as "you" (`LinePage`, `OptionsPage`, the departures
// board, `DisruptionDetailPage`). Putting the chosen set in `prefs` instead would
// have meant a second source of truth for the same list, a name and a line
// list duplicated into it for a watch that cannot read the bundle, and new
// plumbing at every one of those six call sites to answer a question the rows
// already answer. Nothing is added to the row; the row simply stops being
// written by accident.
//
// Pure. It reads what it is handed and hands back a shape; the store, the
// preference bag, the city in force and the Vue refs are all somebody else's
// job.

/**
 * Where the one-time tidy is recorded, per city.
 *
 * ALWAYS SUFFIXED, including in Melbourne, for the reason `placesKey` is: the
 * rows are per city and a person can have used Melbourne for a year and never
 * opened Sydney, so the two are tidied on the day each is first opened rather
 * than both on the day the app updates.
 */
export const CHOSEN_KEY = 'lateagain:chosen:v1';

/** `lateagain:chosen:v1:melbourne`, and the same shape everywhere else. */
export function chosenKey(city) {
  return `${CHOSEN_KEY}:${city}`;
}

/** The row the board opens on, by the rule every screen already uses. */
export function defaultRow(rows = []) {
  const list = Array.isArray(rows) ? rows : [];
  return list.find((r) => Boolean(r?.isDefault)) ?? list[0] ?? null;
}

/**
 * Which of somebody's existing rows survive the collapse.
 *
 * THE ONE THEY OPEN ON, AND NOTHING ELSE.
 *
 * Every row on an existing phone got there by being visited, so none of them
 * was chosen and there is no honest way to tell the one station somebody uses
 * from the four they looked up once. Keeping them all would preserve exactly
 * the noise this change exists to remove — a chosen list nobody chose. Keeping
 * none would take away the board the app opens on, and somebody opening the
 * app to find it pointing at a station they did not expect is the one outcome
 * that is not allowed.
 *
 * So the default survives, which is the row the board was already opening on
 * and the row the widget was already showing, and the rest fall back to RECENT
 * — where the board switcher has been putting them all along, and where they
 * are honestly labelled as somewhere this person has been rather than as
 * somewhere they chose.
 *
 * `defaultRow`'s fallback matters here: a city whose rows somehow carry no
 * `isDefault` keeps its first row, because that is the one the board was
 * showing. This never takes a person's board away.
 *
 * @returns {{ keep: object[], drop: object[] }}
 */
export function collapseToChosen(rows = []) {
  const list = (Array.isArray(rows) ? rows : []).filter(Boolean);
  const keep = defaultRow(list);
  if (!keep) return { keep: [], drop: [] };
  return { keep: [keep], drop: list.filter((r) => r !== keep) };
}

/**
 * Who opens the board once this row is unstarred.
 *
 * Unstarring the default is allowed — refusing it would make the first station
 * somebody ever starred permanent — so the board needs somewhere to go. The
 * next row in the list, which is the next most recently chosen, and null for a
 * set that is now empty. Null is a real answer: the board says NEXT TRAINS and
 * the sheet is one tap away.
 */
export function defaultAfterRemoving(rows = [], id) {
  const left = (Array.isArray(rows) ? rows : []).filter((r) => r && String(r.id) !== String(id));
  if (!left.length) return null;
  return defaultRow(left);
}
